/* ============================================================
   teg — overview.

   The answer to "is anything wrong?" before the answer to
   "what are the numbers?". Anything unhealthy is named at the
   top; the rest of the page is the evidence behind that verdict.
   ============================================================ */

import React, { useEffect, useState } from 'react';
import { Icon, Meter, Spark, fmtBytes, fmtRate, fmtUptime, level, tempLevel } from './lib';
import { api } from './api';

function Stat({ icon, label, value, sub, pct, warn, err, hint }) {
  return (
    <div className="panel tg-stat">
      <div className="tg-stat-top">
        <Icon name={icon} size={15}/>
        <span className="label">{label}</span>
      </div>
      <div className="tg-stat-value">{value}</div>
      {pct != null && <Meter pct={pct} warn={warn} err={err} hint={hint}/>}
      {sub && <div className="meta tg-stat-sub">{sub}</div>}
    </div>
  );
}

export default function Overview({ token, stats, services, games, history, onOpen }) {
  const [failed, setFailed] = useState([]);

  useEffect(() => {
    let live = true;
    const pull = () => api.checks(token)
      .then((d) => { if (live) setFailed(d.failed || []); })
      .catch(() => {});
    pull();
    const id = setInterval(() => { if (!document.hidden) pull(); }, 30000);
    return () => { live = false; clearInterval(id); };
  }, [token]);

  const cpu = stats.cpu || {};
  const mem = stats.memory || {};
  const disk = stats.disk || {};
  const swapPct = stats.swap?.total ? (stats.swap.used / stats.swap.total) * 100 : 0;
  const temps = stats.temps || [];
  const cpuTemp = temps.find((t) => /package|cpu/i.test(t.label))?.current;
  const gpuTemp = temps.find((t) => /gpu/i.test(t.label))?.current;
  const svc = services ? Object.entries(services) : [];
  const down = svc.filter(([, v]) => !v.active);
  const gamesDown = (games || []).filter((g) => !g.running);

  // Everything that would make someone say "hold on".
  const problems = [
    ...down.map(([id, v]) => ({ kind: 'service', text: `${v.desc || id} is not running`, go: 'services' })),
    ...failed.map((f) => {
      // A boot-time oneshot that failed months ago is worth knowing about, but
      // it is not today's emergency — say when, so it reads as history.
      const days = f.since ? Math.floor((Date.now() / 1000 - f.since) / 86400) : null;
      const when = days == null ? '' : days > 1 ? ` (${days} days ago)` : ' (today)';
      return { kind: 'unit', text: `${f.unit} failed${when}`, go: 'system' };
    }),
    ...(disk.percent >= 90 ? [{ kind: 'disk', text: `Root filesystem is ${Math.round(disk.percent)}% full`, go: 'storage' }] : []),
    ...(mem.percent >= 92 ? [{ kind: 'mem', text: `Memory at ${Math.round(mem.percent)}%`, go: 'system' }] : []),
    ...(cpuTemp >= 85 ? [{ kind: 'temp', text: `CPU at ${Math.round(cpuTemp)}°C`, go: 'system' }] : []),
    ...(gpuTemp >= 85 ? [{ kind: 'temp', text: `GPU at ${Math.round(gpuTemp)}°C`, go: 'system' }] : []),
  ];

  return (
    <div className="tg-view">
      <div className={`tg-verdict ${problems.length ? 'is-bad' : 'is-ok'}`}>
        <Icon name={problems.length ? 'warn' : 'check'} size={18}/>
        <div className="tg-verdict-text">
          {problems.length === 0 ? (
            <>
              <strong>Everything is healthy.</strong>
              <span className="meta"> {svc.length} services up · {(games || []).filter((g) => g.running).length} of {(games || []).length} game servers running · up {fmtUptime(stats.uptime)}</span>
            </>
          ) : (
            <>
              <strong>{problems.length} thing{problems.length > 1 ? 's need' : ' needs'} attention</strong>
              <span className="tg-verdict-list">
                {problems.slice(0, 4).map((p) => (
                  <button key={p.text} type="button" className="tg-verdict-item" onClick={() => onOpen(p.go)}>{p.text}</button>
                ))}
              </span>
            </>
          )}
        </div>
      </div>

      <div className="tg-grid tg-grid--stats">
        <Stat icon="cpu" label="CPU" value={`${Math.round(cpu.avg || 0)}%`} pct={cpu.avg}
              hint={`${(cpu.avg || 0).toFixed(1)}% across ${cpu.cores || '?'} cores`}
              sub={`${cpu.cores || '?'} cores · load ${(cpu.load || []).map((l) => l.toFixed(2)).join(' / ')}`}/>
        <Stat icon="memory" label="Memory" value={`${Math.round(mem.percent || 0)}%`} pct={mem.percent}
              hint={`${fmtBytes(mem.used)} used · ${fmtBytes(mem.available)} available`}
              sub={`${fmtBytes(mem.used)} of ${fmtBytes(mem.total)}`}/>
        <Stat icon="disk" label="Root disk" value={`${Math.round(disk.percent || 0)}%`} pct={disk.percent}
              hint={`${fmtBytes(disk.used)} used · ${fmtBytes(disk.free)} free`}
              sub={`${fmtBytes(disk.used)} of ${fmtBytes(disk.total)}`}/>
        <Stat icon="swap" label="Swap" value={`${Math.round(swapPct)}%`} pct={swapPct} warn={40} err={70}
              hint={stats.swap?.total ? `${fmtBytes(stats.swap.used)} of ${fmtBytes(stats.swap.total)}` : 'no swap configured'}
              sub={stats.swap?.total ? `${fmtBytes(stats.swap.used)} of ${fmtBytes(stats.swap.total)}` : 'none configured'}/>
      </div>

      <div className="tg-grid tg-grid--two">
        <div className="panel tg-chart">
          <div className="panel-head"><span className="label">Network</span>
            <span className="meta">↓ {fmtRate(stats.network?.recv_per_s)} · ↑ {fmtRate(stats.network?.sent_per_s)}</span>
          </div>
          <Spark a={history.netIn} b={history.netOut} height={56} label="network throughput"
                 labels={['in', 'out']} format={(v) => fmtRate(v)}/>
        </div>
        <div className="panel tg-chart">
          <div className="panel-head"><span className="label">Disk I/O</span>
            <span className="meta">read {fmtRate(stats.disk_io?.read_per_s)} · write {fmtRate(stats.disk_io?.write_per_s)}</span>
          </div>
          <Spark a={history.dRead} b={history.dWrite} height={56} label="disk throughput"
                 labels={['read', 'write']} format={(v) => fmtRate(v)}/>
        </div>
      </div>

      <div className="tg-grid tg-grid--two">
        <div className="panel">
          <div className="panel-head">
            <span className="label">Services</span>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => onOpen('services')}>All {svc.length}</button>
          </div>
          <ul className="tg-mini">
            {(down.length ? down : svc.slice(0, 6)).map(([id, v]) => (
              <li key={id} className="listrow tg-mini-row">
                <span className={`dot ${v.active ? 'dot--ok' : 'dot--err'}`}/>
                <span className="tg-mini-name">{v.desc || id}</span>
                <span className="meta">{v.active ? (v.memory ? fmtBytes(v.memory) : 'running') : 'stopped'}</span>
              </li>
            ))}
          </ul>
          {down.length === 0 && svc.length > 6 && (
            <div className="meta tg-mini-foot">and {svc.length - 6} more, all running</div>
          )}
        </div>

        <div className="panel">
          <div className="panel-head">
            <span className="label">Game servers</span>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => onOpen('games')}>Manage</button>
          </div>
          <ul className="tg-mini">
            {(games || []).map((g) => (
              <li key={g.id} className="listrow tg-mini-row">
                <span className={`dot ${g.running ? 'dot--ok' : 'dot--err'}`}/>
                <span className="tg-mini-name">{g.name}</span>
                <span className="meta">{g.running ? (g.detail || 'running') : (g.error ? 'unreachable' : 'stopped')}</span>
              </li>
            ))}
            {!games && <li className="tg-mini-row meta">checking…</li>}
          </ul>
          {gamesDown.length > 0 && <div className="meta tg-mini-foot">{gamesDown.length} not running</div>}
        </div>
      </div>

      {temps.length > 0 && (
        <div className="panel">
          <div className="panel-head"><span className="label">Temperatures</span></div>
          <div className="tg-temps">
            {temps.map((t, i) => (
              <div key={`${t.label}-${i}`} className={`tg-temp ${tempLevel(t.current) ? `is-${tempLevel(t.current)}` : ''}`}>
                <span className="meta">{t.label}</span>
                <span className="tg-temp-v">{Math.round(t.current)}°</span>
                {t.critical ? (
                  <Meter pct={(t.current / t.critical) * 100} warn={75} err={90} height={3}
                         hint={`${Math.round(t.current)}°C · limit ${Math.round(t.critical)}°C`}/>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export { level };
